"use client";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ThemeToggle } from "@/common/theme";
import { RootState } from "@/store/store";
import { formatRole } from "@/lib/formatRole";
import { SPLogo } from "@/components/sp-logo";
import {
  BarChart3,
  DollarSign,
  FolderOpen,
  LayoutDashboard,
  ShieldCheck,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { REPORTS_ENABLED } from "@/common/config/featureFlags";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useRef, useState } from "react";
import { useSelector } from "react-redux";

type NavItem = {
  label: string;
  href: string;
  icon: LucideIcon;
  /**
   * Muted pill rendered after the label. Used to mark a section that is
   * reachable but still shows a placeholder, so the click isn't a surprise.
   */
  badge?: string;
};

const baseNavItems: NavItem[] = [
  { label: "Dashboard", href: "/dashboard", icon: LayoutDashboard },
  { label: "Projects", href: "/projects", icon: FolderOpen },
  {
    label: "Reports",
    href: "/reports",
    icon: BarChart3,
    badge: REPORTS_ENABLED ? undefined : "Soon",
  },
];

const pmExtraNavItems: NavItem[] = [
  { label: "Payroll", href: "/payroll", icon: DollarSign },
  { label: "Safety Forms", href: "/safety-forms", icon: ShieldCheck },
];

export default function AppSidebar() {
  const [expanded, setExpanded] = useState(false);
  const pathname = usePathname();
  const liveUser = useSelector((state: RootState) => state.auth.user);
  const lastUserRef = useRef(liveUser);
  if (liveUser) lastUserRef.current = liveUser;
  const user = liveUser ?? lastUserRef.current;
  const isPm = pathname.startsWith("/pm");

  const navItems = [
    ...baseNavItems.map((item) => ({
      ...item,
      href: isPm ? `/pm${item.href}` : item.href,
    })),
    ...(user?.role === "PROJECT_MANAGER"
      ? pmExtraNavItems.map((item) => ({
          ...item,
          href: `/pm${item.href}`,
        }))
      : []),
  ];

  const initials = user?.name
    ? user.name
        .split(" ")
        .map((n: string) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "SP";

  const roleName = formatRole(user?.role) || "User";

  return (
    <aside
      className={`flex h-screen flex-col border-r border-border bg-card transition-all duration-300 overflow-hidden ${
        expanded ? "w-60" : "w-16"
      }`}
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
    >
      {/* Logo */}
      <div className="flex items-center gap-3 px-3.5 py-6">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center">
          <SPLogo variant="icon" size="md" />
        </div>
        {expanded && (
          <div className="leading-tight overflow-hidden whitespace-nowrap">
            <p className="text-sm font-semibold text-foreground">Project</p>
            <p className="text-sm font-semibold text-foreground">Manager</p>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 px-2">
        {navItems.map((item) => {
          const isActive = pathname === item.href || (item.href !== (isPm ? "/pm/dashboard" : "/dashboard") && pathname.startsWith(item.href));
          return (
            <Link
              key={item.href}
              href={item.href}
              className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive
                  ? "bg-accent text-accent-foreground"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              }`}
            >
              <item.icon className="size-5 shrink-0" />
              {expanded && (
                <>
                  <span className="whitespace-nowrap">{item.label}</span>
                  {item.badge && (
                    <span className="ml-auto shrink-0 rounded-full border border-border px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {item.badge}
                    </span>
                  )}
                </>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Theme toggle — persistent control reachable from every PM screen */}
      <div className="px-2 pb-2">
        <div
          className={`flex items-center ${
            expanded ? "justify-between gap-3 px-1" : "justify-center"
          }`}
        >
          {expanded && (
            <span className="text-xs font-medium text-muted-foreground">Theme</span>
          )}
          <ThemeToggle />
        </div>
      </div>

      {/* User profile — opens the settings/profile page */}
      <div className="border-t border-border px-2 py-4">
        <Link
          href="/pm/settings/profile"
          aria-label="Profile and settings"
          className={`flex w-full items-center gap-3 rounded-lg transition-colors ${
            expanded ? "px-1 py-1" : "justify-center py-1"
          } ${
            pathname.startsWith("/pm/settings")
              ? "bg-accent text-accent-foreground"
              : "hover:bg-muted"
          }`}
        >
          <Avatar className="h-9 w-9 shrink-0">
            <AvatarFallback className="bg-muted text-xs text-foreground">
              {initials}
            </AvatarFallback>
          </Avatar>
          {expanded && (
            <div className="flex-1 overflow-hidden">
              <p className="truncate text-sm font-medium text-foreground">
                {user?.name ?? "User"}
              </p>
              <p className="truncate text-xs capitalize text-muted-foreground">
                {roleName}
              </p>
            </div>
          )}
        </Link>
      </div>
    </aside>
  );
}
